<template>
  <div>
    <div class="tile my-4 px-3 py-4">
      <flow-group :flowGroup="flowGroup"/>
    </div>

    <div class="tile my-4">
      <loading-view :loading="initialLoading">
        <div v-if="showSavedEntities">
          <div class="card">
            <div class="card-body">
              <div class="card-title">
                {{ trans('dashboard.integrations.flows.admin.entities.title', {flow: flowName, count: totalEntities}) }}
              </div>
              <table class="table w-full mt-6" aria-label="List of saved orders">
                <thead>
                <tr>
                  <th scope="col" class="w-1/6">
                    <select class="form-control form-select form-control-sm" name="actions" @change="executeAction"
                            v-model="actionToPerform"
                            :disabled="entitiesToPerformAction.length <= 0">
                      <option :value=null selected disabled>{{ trans('dashboard.integrations.flows.select_an_action') }}</option>
                      <option value="delete">{{ trans('common.delete') }}</option>
                      <option v-if="displayRetryFunction" value="retry">{{ trans('common.retry') }}</option>
                      <option v-if="hasSelectedAll" value="delete_all">{{ trans('dashboard.integrations.flows.admin.delete_all') }}</option>
                    </select>
                  </th>
                  <th scope="col" class="w-1/3">
                    <input
                      :value="filters.entity_id"
                      class="form-input-bordered form-control form-control-sm"
                      @change="(event) => onFilterChange({ entity_id: event.target.value })"
                    />
                  </th>
                  <th scope="col" class="w-1/6"></th>
                  <th scope="col" class="w-1/6">
                    <select
                      slot="select"
                      :value="filters.status"
                      class="block w-full form-control-sm form-select"
                      @change="event => onFilterChange({ status: event.target.value })"
                    >
                      <option v-for="(status, index) in statuses" :key="index" :value="status">
                        {{ status === '—' ? '—' : trans(`activity.status.${status}`) }}
                      </option>
                    </select>
                  </th>
                  <th scope="col" class="w-1/6 text-center">
                    <a
                      class="btn btn-success btn-sm"
                      v-text="trans('common.export')"
                      :href="route('api.integrations.flows.admin.entities.export', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id])"/>
                  </th>
                </tr>
                <tr>
                  <th scope="col" class="text-center">
                    <input type="checkbox" :checked="hasSelectedAll"
                           @change="toggleSelectAll" :disabled="!this.entities.length"/>
                  </th>
                  <th scope="col" class="text-center">{{ flowName + ' ' + trans('common.id') }}</th>
                  <th scope="col" class="text-left">{{ trans('dashboard.integrations.flows.admin.entities.latest_state') }}</th>
                  <th scope="col" class="text-left">{{ trans('dashboard.integrations.flows.admin.entities.last_send_status') }}</th>
                  <th scope="col" class="text-center">{{ trans('common.action') }}</th>
                </tr>
                </thead>

                <tbody>
                <tr v-if="loadingEntities">
                  <td colspan="8">
                    <div class="text-center">
                      <loader class="text-60"/>
                    </div>
                  </td>
                </tr>
                <tr v-for="entity in entities" v-else :key="entity.id">
                  <td>
                    <div class="text-center">
                      <input type="checkbox" v-model="entitiesToPerformAction" :value="entity.entity_id"/>
                    </div>
                  </td>
                  <td>
                    <div class="text-center">
                      <span class="whitespace-no-wrap">{{ entity.entity_id }}</span>
                    </div>
                  </td>
                  <td>
                    <ul class="list-unstyled">
                      <li>{{ entity.latest_state.created_at }}</li>
                      <li><em>{{ entity.latest_state.metadata }}</em></li>
                    </ul>
                  </td>
                  <td>
                    <div class="text-center status">
                      <i
                        :class="{
                          'fa-check-square success': entity.last_activity_status === 'success',
                          'fa-times-circle failed': entity.last_activity_status === 'failed',
                          'fa-exclamation-triangle warning': entity.last_activity_status === 'warning',
                          'fa-retweet pending': entity.last_activity_status === 'pending',
                          'fa-pause-circle-o pause': entity.last_activity_status === 'pause',
                          'fa-question-circle debug': entity.last_activity_status === null,
                        }"
                        aria-hidden="true"
                        class="fa fa-2x"
                      ></i>
                    </div>
                  </td>
                  <td>
                    <div class="text-center">
                      <form-button
                        button-class="btn btn-primary btn-sm mr-3"
                        role="button"
                        v-if="displayRetryFunction"
                        :value="trans('common.retry')"
                        :processing="loadingRetry"
                        @click="retrySingleEntity(entity.entity_id)"/>
                      <form-button
                        button-class="btn btn-danger btn-sm"
                        role="button"
                        :value="trans('common.delete')"
                        :processing="loadingDelete"
                        @click="deleteSingleEntity(entity.entity_id)"/>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
              <div class="flex justify-content-between align-items-center">
                <div v-if="entities.length">
                  <select
                    name="perPage"
                    class="inline-block form-control-sm form-select"
                    :value="filters.perPage"
                    @change="event => onFilterChange({ perPage: event.target.value })"
                  >
                    <option v-for="perPage in perPages" :key="perPage" :value="perPage">{{ perPage }}</option>
                  </select>
                  <label class="ml-2">{{ trans('pagination.per_page') }}</label>
                </div>
                <pagination v-if="pagination" :pagination="pagination" @paginate="onFilterChange"/>
              </div>
            </div>
          </div>
        </div>
        <FlowScheduledRetry :user="user" :flowName="flowName"/>
        <div>
          <div class="card">
            <div class="card-body">
              <div class="card-title">{{ trans('dashboard.integrations.flows.admin.trigger_manual.title') }}</div>
              <div class="card-subtitle">
                {{ trans('dashboard.integrations.flows.admin.trigger_manual.description') }}
              </div>
              <button type="button" class="btn btn-primary" @click="triggerFlow([])">
                {{ trans('dashboard.integrations.flows.admin.trigger_manual.title') }}
              </button>
            </div>
          </div>
          <div class="card-deck">
            <div class="card">
              <div class="card-body">
                <div class="card-title">{{ trans('dashboard.integrations.flows.admin.cache_file.title') }}</div>
                <div v-if="!showCachingFileRemoveButton">
                  <div class="card-subtitle">
                    {{ trans('dashboard.integrations.flows.admin.cache_file.no_files') }}
                  </div>
                </div>
                <div v-else-if="checkedCacheFileExists">
                  <div class="card-subtitle">
                    {{ trans('dashboard.integrations.flows.admin.cache_file.description') }}
                  </div>
                  <button type="button" class="btn btn-danger" @click="triggerRemoveCacheFile()">
                    {{ trans('dashboard.integrations.flows.admin.cache_file.title') }}
                  </button>
                </div>
                <div v-else>
                  <div v-if="! this.hasErrorAfterFetchingCachingFile" class="card-subtitle">
                    {{ trans('dashboard.integrations.flows.admin.cache_file.fetching_in_progress') }}
                  </div>
                  <div v-else class="card-subtitle">
                    {{ trans('dashboard.integrations.flows.admin.cache_file.fetching_failed') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card">
            <div class="card-body">
              <div class="card-title">{{ trans('dashboard.integrations.flows.admin.debug_mode.title') }}</div>
              <div>
                <input
                  id="check_lock_debug_mode"
                  class="form_knob_check"
                  name="remember"
                  type="checkbox"
                  :checked="this.userIntegrationFlow.lock_debug_mode"
                  @click="toggleDebugLock"/>
                <label class="form_knob mb-0 text_normal" for="check_lock_debug_mode">
                  {{ trans('dashboard.integrations.flows.admin.debug_mode.description') }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </loading-view>
    </div>
    <confirmation-modal
      :show-modal="confirmingAction"
      :header="trans('dashboard.integrations.flows.confirm_action')"
      :content="trans(`dashboard.integrations.flows.${actionToPerform}_items_confirmation_description`)"
      :confirm-button-text="confirmActionButton"
      :type="['delete', 'delete_all'].includes(actionToPerform) ? 'danger' : 'primary'"
      @close="handleCloseActionModal"
      @confirm="handleConfirmAction"
    />
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import Swal from "sweetalert2";
import DashboardLayout from "@/Layouts/DashboardLayout";
import IntegrationFlowLayout from "@/Layouts/IntegrationFlowLayout";
import Pagination from '@/components/Shared/Pagination';
import FlowGroup from "@/components/Dashboard/FlowGroup";
import Loader from "@/components/Shared/Loader";
import FormButton from "@/components/Shared/Form/Button";
import ConfirmationModal from "@/components/Dashboard/ConfirmationModal";
import FlowScheduledRetry from "@/Pages/Dashboard/Integrations/Flows/Partials/FlowScheduledRetry.vue";

export default {
  name: 'FlowAdmin',

  layout: [DashboardLayout, IntegrationFlowLayout],

  components: {
    FlowScheduledRetry,
    ConfirmationModal,
    FormButton,
    Loader,
    FlowGroup,
    Pagination,
  },

  props: {
    user: Object
  },

  data() {
    return {
      initialLoading: true,
      loadingEntities: false,
      loadingDelete: false,
      loadingRetry: false,
      entities: [],
      totalEntities: "-",
      flowName: '',
      hasCachingFile: null,
      hasErrorAfterFetchingCachingFile: false,
      pagination: null,
      toBeDeleted: null,
      entitiesToPerformAction: [],
      actionToPerform: null,
      confirmingAction: false,
      hasSelectedAll: false,
      statuses: ['—', 'success', 'warning', 'pending', 'failed', 'unknown'],
      perPages: [15, 25, 50, 75, 100, 150, 200, 250],
    };
  },

  async beforeMount() {
    Promise
      .all([this.getEntities(), this.checkCacheFileExists()])
      .then(() => this.initialLoading = false)

    this.setFlowName();
  },

  computed: {
    ...mapGetters('flowsDetails', ['flowGroup', 'userIntegrationFlow']),

    filters() {
      const {page, perPage, entity_id = null, status = null} = this.$route().params;
      return {
        page: Number(page || '1'),
        perPage: perPage ? parseInt(perPage) : this.perPages[0],
        entity_id: entity_id,
        status: status !== '—' ? status : null,
      };
    },

    showSavedEntities() {
      return this.entities.length > 0 || this.filters.entity_id !== null || this.filters.status !== null || this.toBeDeleted !== null;
    },

    showCachingFileRemoveButton() {
      return this.hasCachingFile === null || this.hasCachingFile === true;
    },

    checkedCacheFileExists() {
      return this.hasCachingFile !== null;
    },

    displayRetryFunction() {
      return this.flowName === 'SalesOrder';
    },

    confirmActionButton() {
      switch (this.actionToPerform) {
        case 'delete_all':
          return this.trans('dashboard.integrations.flows.admin.delete_all')
        default:
          return this.trans(`common.${this.actionToPerform}`)
      }
    },
  },

  watch: {
    entitiesToPerformAction() {
      this.hasSelectedAll = this.entities.length > 0 && this.entitiesToPerformAction.length === this.entities.length;
    },
  },

  methods: {
    async getEntities() {
      this.loadingEntities = true;

      return axios
        .get(route('api.integrations.flows.admin.entities.index', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id]), {
          params: {...this.filters, ...(this.additionalFilters || {})},
        })
        .then(({data}) => {
          const {data: entities, ...rest} = data;
          this.entities = entities;
          this.pagination = rest;
          this.totalEntities = rest.total;
        })
        .finally(() => this.loadingEntities = false);
    },

    async checkCacheFileExists() {
      return axios
        .get(route('api.integrations.flows.admin.caching.index', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id]))
        .then(({data}) => {
          this.hasCachingFile = data['hasCachingFile'];
        })
        .catch(() => {
          this.hasErrorAfterFetchingCachingFile = true
        });
    },

    onFilterChange(data) {
      const filters = {...this.filters, ...data};
      const parameters = this.$route().params

      filters['page'] = data['page'] ?? 1

      Object.keys(filters).forEach(filter => {
        delete parameters[filter]

        let filterValue = filters[filter]

        if (filterValue !== null && filterValue !== '—') {
          parameters[filter] = filterValue;
        }
      });

      this.$inertia.visit(
        this.$route(this.$route().current(), parameters),
      )
    },

    async deleteEntities(entityIds) {
      this.toBeDeleted = entityIds;
      this.loadingDelete = true;
      this.entities = entityIds !== 'ALL' ?
        _.filter(this.entities, entity => !entityIds.includes(entity.entity_id))
        : [];

      await axios
        .delete(route('api.integrations.flows.admin.entities.delete', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id]), {data: {entityIds}})
        .then(() => this.loadingDelete = false);
    },

    async triggerFlow(orderIds = []) {
      this.loadingRetry = true;

      await axios
        .post(route('api.integrations.flows.admin.manual_trigger', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id]), {orderIds})
        .then(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.success_trigger_flow'),
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
          });
        })
        .catch(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.failed_trigger_flow'),
            icon: 'error',
            showConfirmButton: false,
            timer: 2000,
          });
        })
        .finally(() => this.loadingRetry = false);
    },

    async triggerRemoveCacheFile() {
      await axios
        .delete(route('api.integrations.flows.admin.caching.delete', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id]))
        .then(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.success_remove_caching_file'),
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
          });
          this.hasCachingFile = false;
        })
        .catch(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.failed_remove_caching_file'),
            icon: 'error',
            showConfirmButton: false,
            timer: 2000,
          });
        });
    },

    async toggleDebugLock() {
      await axios
        .post(route('api.integrations.flows.admin.toggle_lock_debug_mode', [this.userIntegrationFlow.user_integration_id, this.userIntegrationFlow.id]))
        .then(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.success_toggle_debug_mode'),
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
          });
        })
        .catch(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.failed_toggle_debug_mode'),
            icon: 'error',
            showConfirmButton: false,
            timer: 2000,
          });
        });
    },
    executeAction(event) {
      this.actionToPerform = event.target.value;
      this.confirmingAction = true;
    },

    retrySingleEntity(entityId) {
      this.entitiesToPerformAction = [entityId];
      this.actionToPerform = 'retry';
      this.confirmingAction = true;
    },

    deleteSingleEntity(entityId) {
      this.entitiesToPerformAction = [entityId];
      this.actionToPerform = 'delete';
      this.confirmingAction = true;
    },

    handleConfirmAction() {
      if (['delete', 'delete_all'].includes(this.actionToPerform)) {
        this.deleteEntities(this.actionToPerform === 'delete_all' ? 'ALL' : this.entitiesToPerformAction);
      } else if (this.actionToPerform === 'retry') {
        this.triggerFlow(this.entitiesToPerformAction);
      }

      this.handleCloseActionModal();
    },

    handleCloseActionModal() {
      this.confirmingAction = false;
      this.actionToPerform = null;
      this.entitiesToPerformAction = [];
    },

    toggleSelectAll() {
      this.entitiesToPerformAction = this.hasSelectedAll ? [] : this.entities.map((entity) => entity.entity_id)
    },

    setFlowName() {
      let name = this.userIntegrationFlow.first_flow.name

      let start = 4;
      let end = 0;
      if (name.substring(0, 3) === 'get') {
        start = 3
      }

      if (name.slice(-5) === 'Batch') {
        end = 5
      }

      this.flowName = name.substring(start, name.length - end);
    },
  },
};
</script>
<style lang="scss">
.status {
  font-size: 1.6rem;

  i {
    &.failed {
      color: var(--danger);
    }

    &.success {
      color: var(--green);
    }

    &.pending,
    &.warning {
      color: #e5ce00;
    }

    &.pause {
      color: black;
    }

    &.debug {
      color: var(--info);
    }
  }
}
</style>
